import {
    ActionIcon,
    Alert,
    Badge,
    Group,
    Loader,
    Stack,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import {AiOutlineCloudServer, AiOutlineReload} from 'react-icons/ai';
import {PlatformPropertyCard} from '@flexinet/ui-components';
import {useStackStatusQuery} from '../hooks/usePipelinesQuery';

const statusColor = (status: string): string => {
    if (status.endsWith('_COMPLETE') && !status.includes('ROLLBACK') && !status.includes('FAILED')) {
        return 'green';
    }
    if (status.includes('IN_PROGRESS')) {
        return 'blue';
    }
    if (status.includes('ROLLBACK') || status.includes('FAILED')) {
        return 'red';
    }
    if (status.includes('DELETE_COMPLETE')) {
        return 'gray';
    }
    return 'yellow';
};

const isTransitional = (status?: string) =>
    !!status && status.includes('IN_PROGRESS');

const isFailure = (status?: string) =>
    !!status && (status.includes('FAILED') || status.includes('ROLLBACK'));

interface StackStatusPanelProps {
    stackName?: string;
}

export const StackStatusPanel = ({stackName}: StackStatusPanelProps) => {

    const {
        data: stackStatus,
        isLoading,
        isError,
        error,
        refetch,
        isFetching,
    } = useStackStatusQuery(stackName);

    if (!stackName) {
        return null;
    }

    const properties: { label: string; value: string | React.ReactElement; span: number }[] = [
        {
            label: 'Stack Name',
            value: stackName,
            span: 12,
        },
    ];

    if (stackStatus) {
        properties.push({
            label: 'Status',
            value: (
                <Badge
                    color={statusColor(stackStatus.status)}
                    variant={isFailure(stackStatus.status) ? 'filled' : 'light'}
                >
                    {stackStatus.status}
                </Badge>
            ),
            span: 12,
        });

        if (stackStatus.statusReason) {
            properties.push({
                label: 'Status Reason',
                value: (
                    <Text size='sm' style={{whiteSpace: 'pre-wrap'}}>
                        {stackStatus.statusReason}
                    </Text>
                ),
                span: 12,
            });
        }
    }

    return (
        <Stack gap='sm'>
            <Group justify='space-between' align='center' wrap='nowrap'>
                <Group gap='xs'>
                    <AiOutlineCloudServer size={20}/>
                    <Title order={5}>CloudFormation stack</Title>
                    {isTransitional(stackStatus?.status) && (
                        <Tooltip label='Auto-refreshing every 15s while stack is transitioning'>
                            <Loader size='xs'/>
                        </Tooltip>
                    )}
                </Group>
                <Tooltip label='Refresh'>
                    <ActionIcon
                        variant='subtle'
                        onClick={() => refetch()}
                        loading={isFetching}
                        aria-label='Refresh stack status'
                    >
                        <AiOutlineReload/>
                    </ActionIcon>
                </Tooltip>
            </Group>

            <PlatformPropertyCard properties={properties}/>

            {isLoading && (
                <Group gap='xs'>
                    <Loader size='sm'/>
                    <Text size='sm' c='dimmed'>Loading status…</Text>
                </Group>
            )}

            {isError && (
                <Alert color='red' variant='light'>
                    <Text size='sm'>
                        Could not load stack status
                        {error instanceof Error ? `: ${error.message}` : '.'}
                    </Text>
                </Alert>
            )}
        </Stack>
    );
};
